import React, { useState, useEffect } from 'react';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink } from '@chakra-ui/react';
import { useLocation, Link } from 'react-router-dom';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { getAdminUserDetail } from '../services/api';

const pathNameMapping = {
  equipment: '設備選択',
  planner: '間取り作成',
  'visit-reservation': '来店予約',
  'confirmed-summary': '概算見積',
  'like-images': 'お気に入り画像',
  chat: 'ご相談',
  guide: '使い方ガイド',
  admin: '管理者',
  users: 'お客様一覧',
  user: 'ユーザー詳細',
  'user-info': 'ユーザー情報',
  'master-equipment': 'マスタ管理',
  'master-equipment-definitions': '設備定義管理',
  'master-floor': '間取りマスタ',
  'excluded-items': '見積もり外項目管理',
};

const BreadcrumbComponent = () => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);
  const [userDisplayName, setUserDisplayName] = useState('');

  // URLからtargetUserIdを抽出
  const extractUserId = () => {
    const userIndex = pathnames.indexOf('user');
    if (userIndex !== -1 && userIndex + 1 < pathnames.length) {
      return pathnames[userIndex + 1];
    }
    return null;
  };

  // targetUserIdがある場合にユーザー情報を取得
  useEffect(() => {
    const fetchUserInfo = async () => {
      const targetUserId = extractUserId();
      if (targetUserId) {
        try {
          const userData = await getAdminUserDetail(targetUserId);
          const displayName = userData.name ? `${userData.name} 様邸` : `${userData.email} 様邸`;
          setUserDisplayName(displayName);
        } catch (error) {
          console.error('ユーザー情報の取得に失敗:', error);
          setUserDisplayName('ユーザー詳細');
        }
      } else {
        setUserDisplayName('');
      }
    };

    fetchUserInfo();
  }, [location.pathname]);

  // ホームのみの場合は表示しない
  if (pathnames.length === 0) return null;

  let currentPath = '';
  const breadcrumbItems = [];
  
  // パンくずアイテムを構築
  for (let idx = 0; idx < pathnames.length; idx++) {
    const name = pathnames[idx];
    currentPath += `/${name}`;
    
    // 管理者の後にユーザー一覧を追加
    if (name === 'admin' && idx + 1 < pathnames.length && pathnames[idx + 1] === 'user') {
      breadcrumbItems.push({
        name: pathNameMapping[name] || name,
        path: currentPath,
        isCurrentPage: false,
      });
      // ユーザー一覧を追加
      breadcrumbItems.push({
        name: 'お客様一覧',
        path: '/admin/users',
        isCurrentPage: false,
      });
      continue;
    }
    
    // 動的パラメータ（:userId, :targetUserId）は「◎様邸」として表示
    if (name === 'user' && idx + 1 < pathnames.length) {
      const targetUserId = pathnames[idx + 1];
      breadcrumbItems.push({
        name: userDisplayName || 'ユーザー詳細',
        path: `/admin/user/${targetUserId}`,
        isCurrentPage: idx === pathnames.length - 2,
      });
      idx++; // 次のパラメータをスキップ
      continue;
    }
    
    // 設備選択の場合は◎様邸と同じリンクにする
    if (name === 'equipment' && pathnames.includes('user')) {
      const userIndex = pathnames.indexOf('user');
      const targetUserId = pathnames[userIndex + 1];
      breadcrumbItems.push({
        name: pathNameMapping[name] || name,
        path: `/admin/user/${targetUserId}`,
        isCurrentPage: idx === pathnames.length - 1,
      });
    }
    // 確定アイテム一覧の場合は正しいパスにする
    else if (name === 'confirmed-summary' && pathnames.includes('user')) {
      const userIndex = pathnames.indexOf('user');
      const targetUserId = pathnames[userIndex + 1];
      breadcrumbItems.push({
        name: pathNameMapping[name] || name,
        path: `/admin/user/${targetUserId}/equipment/confirmed-summary`,
        isCurrentPage: idx === pathnames.length - 1,
      });
    }
    else {
      breadcrumbItems.push({
        name: pathNameMapping[name] || name,
        path: currentPath,
        isCurrentPage: idx === pathnames.length - 1,
      });
    }
  }

  return (
    <Breadcrumb
      spacing="8px"
      separator={<ChevronRightIcon color="gray.500" />}
      mb={4}
      px={4}
      py={2}
      bg="gray.50"
      borderRadius="md"
    >
      <BreadcrumbItem>
        <BreadcrumbLink as={Link} to="/" color="gray.600">
          ホーム
        </BreadcrumbLink>
      </BreadcrumbItem>
      {breadcrumbItems.map((item, idx) => (
        <BreadcrumbItem key={idx} isCurrentPage={item.isCurrentPage}>
          <BreadcrumbLink
            as={Link}
            to={item.path}
            color={item.isCurrentPage ? 'gray.800' : 'gray.600'}
            fontWeight={item.isCurrentPage ? 'bold' : 'normal'}
          >
            {item.name}
          </BreadcrumbLink>
        </BreadcrumbItem>
      ))}
    </Breadcrumb>
  );
};

export default BreadcrumbComponent; 